import { useCallback, useContext } from 'react';

import useCrossProductUrlWrapper from '@atlassiansox/analytics-cross-product-interaction-client/use-cross-product-url-wrapper';
import { di } from 'react-magnetic-di';

import { SmartCardContext } from '@atlaskit/link-provider/context';
import { fg } from '@atlaskit/platform-feature-flags/fg';

/** XPC navigation surface identifier (capability-surface, e.g. rovoAgents-agentsList). */
export type CrossProductBridge = string;

export type AgentCrossProductUrlOptions = {
	xpcBridge: CrossProductBridge;
	gate?: 'rsa-1417-ai-mate-xpc-surfaces';
};

const identity = (url: string): string => url;

const isRovoChatUrl = (pathname: string): boolean =>
	pathname === '/chat' || /^\/o\/[^/]+\/chat(?:\/|$)/.test(pathname);

const isSameProductUrl = (url: string, xpcProduct: string): boolean => {
	if (typeof window === 'undefined') {
		return false;
	}

	try {
		const destination = new URL(url, window.location.href);
		if (destination.origin !== window.location.origin) {
			return false;
		}

		if (xpcProduct === 'confluence') {
			return destination.pathname === '/wiki' || destination.pathname.startsWith('/wiki/');
		}

		if (xpcProduct === 'jira') {
			return destination.pathname !== '/wiki' && !destination.pathname.startsWith('/wiki/');
		}

		if (xpcProduct === 'townsquare') {
			return !isRovoChatUrl(destination.pathname);
		}

		return true;
	} catch {
		return false;
	}
};

/**
 * Stamps agent URLs with XPC attribution for the host product they are clicked in, read from
 * `SmartCardProvider`'s `xpcProduct`. Falls back to an identity wrapper when no host product is
 * available, so a navigation is never attributed to the wrong one. `ai-mate-xpc` is the kill-switch.
 */
export const useAgentCrossProductUrlWrapper = ({
	xpcBridge,
	gate,
}: AgentCrossProductUrlOptions): ((url: string) => string) => {
	di(useCrossProductUrlWrapper);

	// Not `useSmartLinkContext()`, which throws without a provider — these render outside one.
	const cardContext = useContext(SmartCardContext);
	const xpcProduct = cardContext?.xpcProduct;

	// Called unconditionally for the Rules of Hooks; the result goes unused when returning identity.
	const wrapUrl = useCrossProductUrlWrapper({
		bridge: xpcBridge,
		product: xpcProduct ?? '',
	});

	const wrapCrossProductUrl = useCallback(
		(url: string) => (isSameProductUrl(url, xpcProduct ?? '') ? url : wrapUrl(url)),
		[wrapUrl, xpcProduct],
	);

	if (!xpcProduct || !fg('ai-mate-xpc') || (gate && !fg(gate))) {
		return identity;
	}

	return wrapCrossProductUrl;
};
